import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

interface Hashtag {
  tag: string;
  reach: string;
  type: 'trending' | 'moderate' | 'niche';
}

export default function HashtagGenerator() {
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('general');
  const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  const categories = [
    { value: 'general', label: 'General', icon: 'ri-global-line' },
    { value: 'fashion', label: 'Fashion', icon: 'ri-shirt-line' },
    { value: 'food', label: 'Food', icon: 'ri-restaurant-line' },
    { value: 'fitness', label: 'Fitness', icon: 'ri-run-line' },
    { value: 'travel', label: 'Travel', icon: 'ri-flight-takeoff-line' },
    { value: 'business', label: 'Business', icon: 'ri-briefcase-line' },
    { value: 'tech', label: 'Tech', icon: 'ri-smartphone-line' },
    { value: 'art', label: 'Art', icon: 'ri-palette-line' },
  ];

  // Simulated hashtag generation
  const generateHashtags = () => {
    if (!topic.trim()) return;

    setIsGenerating(true);
    setHashtags([]);
    setCopiedAll(false);

    // Simulate API call
    setTimeout(() => {
      const hashtagData: { [key: string]: Hashtag[] } = {
        general: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '1.2M', type: 'trending' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}life`, reach: '850K', type: 'trending' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}love`, reach: '650K', type: 'moderate' },
          { tag: `#dailyinspiration`, reach: '12M', type: 'trending' },
          { tag: '#instagood', reach: '1.8B', type: 'trending' },
          { tag: '#photooftheday', reach: '1.2B', type: 'trending' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}vibes`, reach: '420K', type: 'moderate' },
          { tag: `#my${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '180K', type: 'niche' },
          { tag: '#motivation', reach: '580M', type: 'trending' },
          { tag: '#lifestyle', reach: '450M', type: 'trending' },
        ],
        fashion: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}fashion`, reach: '2.5M', type: 'trending' },
          { tag: '#ootd', reach: '450M', type: 'trending' },
          { tag: '#fashionista', reach: '180M', type: 'trending' },
          { tag: '#style', reach: '620M', type: 'trending' },
          { tag: '#instafashion', reach: '220M', type: 'moderate' },
          { tag: '#fashionblogger', reach: '95M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}style`, reach: '1.8M', type: 'moderate' },
          { tag: '#outfitoftheday', reach: '140M', type: 'moderate' },
          { tag: '#streetstyle', reach: '85M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}trends`, reach: '420K', type: 'niche' },
        ],
        food: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '3.2M', type: 'trending' },
          { tag: '#foodie', reach: '280M', type: 'trending' },
          { tag: '#foodporn', reach: '310M', type: 'trending' },
          { tag: '#instafood', reach: '250M', type: 'trending' },
          { tag: '#yummy', reach: '180M', type: 'trending' },
          { tag: '#delicious', reach: '120M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}lover`, reach: '2.1M', type: 'moderate' },
          { tag: '#foodphotography', reach: '95M', type: 'moderate' },
          { tag: '#foodblogger', reach: '88M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}time`, reach: '850K', type: 'niche' },
        ],
        fitness: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '5.8M', type: 'trending' },
          { tag: '#fitness', reach: '580M', type: 'trending' },
          { tag: '#fitnessmotivation', reach: '180M', type: 'trending' },
          { tag: '#workout', reach: '320M', type: 'trending' },
          { tag: '#gym', reach: '250M', type: 'trending' },
          { tag: '#fitspo', reach: '85M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}journey`, reach: '1.2M', type: 'moderate' },
          { tag: '#healthylifestyle', reach: '95M', type: 'moderate' },
          { tag: '#fitfam', reach: '78M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}goals`, reach: '640K', type: 'niche' },
        ],
        travel: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '4.2M', type: 'trending' },
          { tag: '#travel', reach: '680M', type: 'trending' },
          { tag: '#wanderlust', reach: '180M', type: 'trending' },
          { tag: '#travelgram', reach: '150M', type: 'trending' },
          { tag: '#instatravel', reach: '120M', type: 'trending' },
          { tag: '#travelphotography', reach: '95M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}travel`, reach: '2.8M', type: 'moderate' },
          { tag: '#explore', reach: '420M', type: 'trending' },
          { tag: '#adventure', reach: '280M', type: 'trending' },
          { tag: `#visit${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '580K', type: 'niche' },
        ],
        business: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}business`, reach: '3.5M', type: 'trending' },
          { tag: '#entrepreneur', reach: '180M', type: 'trending' },
          { tag: '#business', reach: '280M', type: 'trending' },
          { tag: '#marketing', reach: '150M', type: 'trending' },
          { tag: '#success', reach: '220M', type: 'trending' },
          { tag: '#businessowner', reach: '45M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}tips`, reach: '1.8M', type: 'moderate' },
          { tag: '#entrepreneurship', reach: '88M', type: 'moderate' },
          { tag: '#businessmindset', reach: '28M', type: 'niche' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}strategy`, reach: '650K', type: 'niche' },
        ],
        tech: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '2.8M', type: 'trending' },
          { tag: '#tech', reach: '150M', type: 'trending' },
          { tag: '#technology', reach: '180M', type: 'trending' },
          { tag: '#innovation', reach: '95M', type: 'trending' },
          { tag: '#digital', reach: '120M', type: 'trending' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}tech`, reach: '1.5M', type: 'moderate' },
          { tag: '#techlife', reach: '42M', type: 'moderate' },
          { tag: '#gadgets', reach: '35M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}innovation`, reach: '850K', type: 'niche' },
          { tag: '#futuretech', reach: '12M', type: 'moderate' },
        ],
        art: [
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}`, reach: '3.1M', type: 'trending' },
          { tag: '#art', reach: '580M', type: 'trending' },
          { tag: '#artist', reach: '320M', type: 'trending' },
          { tag: '#artwork', reach: '180M', type: 'trending' },
          { tag: '#instaart', reach: '95M', type: 'trending' },
          { tag: '#creative', reach: '150M', type: 'trending' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}art`, reach: '2.2M', type: 'moderate' },
          { tag: '#artistsoninstagram', reach: '88M', type:'moderate' },
          { tag: '#artoftheday', reach: '65M', type: 'moderate' },
          { tag: `#${topic.toLowerCase().replace(/\s+/g, '')}artist`, reach: '1.1M', type: 'niche' },
        ],
      };

      const selectedHashtags = hashtagData[category] || hashtagData.general;
      setHashtags(selectedHashtags);
      setIsGenerating(false);
      setShowEmailPrompt(true);
    }, 1500);
  };

  const copyAllHashtags = () => {
    const allTags = hashtags.map(h => h.tag).join(' ');
    navigator.clipboard.writeText(allTags);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const copyHashtag = (tag: string) => {
    navigator.clipboard.writeText(tag);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'trending':
        return 'bg-green-100 text-green-700';
      case 'moderate':
        return 'bg-blue-100 text-blue-700';
      case 'niche':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'trending':
        return 'ri-fire-line';
      case 'moderate':
        return 'ri-arrow-up-line';
      case 'niche':
        return 'ri-star-line';
      default:
        return 'ri-hashtag';
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a
            href="/tools"
            className="inline-flex items-center gap-2 text-[#22577A] font-medium mb-6 hover:text-[#38a3a5] transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Tools
          </a>

          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-hashtag mr-2"></i>
            AI Hashtag Generator
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Find Trending
            <span className="block text-[#38a3a5]">Hashtags Instantly</span>
          </h1>

          <p className="text-xl text-gray-700 mb-8">
            Boost your reach with relevant, trending hashtags tailored to your content
          </p>
        </div>
      </section>

      {/* Generator Tool */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-white rounded-3xl p-8 shadow-xl border-2 border-[#b3d9ff]">
            {/* Input Section */}
            <div className="space-y-6 mb-8">
              {/* Topic Input */}
              <div>
                <label className="block text-sm font-semibold text-[#22577A] mb-2">
                  What's your content about?
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="E.g., coffee, workout, sunset, marketing..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#66b3ff] focus:outline-none text-gray-800"
                />
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-semibold text-[#22577A] mb-2">
                  Choose your category
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat.value)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        category === cat.value
                          ? 'border-[#22577A] bg-[#e4f4ff]'
                          : 'border-gray-200 hover:border-[#66b3ff]'
                      }`}
                    >
                      <i className={`${cat.icon} text-2xl text-[#22577A]`}></i>
                      <span className="text-sm font-medium text-gray-800">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateHashtags}
                disabled={!topic.trim() || isGenerating}
                className="w-full py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white font-semibold rounded-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Generating Hashtags...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-sparkling-line"></i>
                    Generate Hashtags
                  </span>
                )}
              </button>
            </div>

            {/* Results Section */}
            {hashtags.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-[#22577A]">
                    Your Hashtags ({hashtags.length}):
                  </h3>
                  <button
                    onClick={copyAllHashtags}
                    className="flex items-center gap-2 px-4 py-2 bg-[#22577A] text-white rounded-lg font-medium hover:bg-[#38a3a5] transition-all"
                  >
                    <i className={copiedAll ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                    {copiedAll ? 'Copied!' : 'Copy All'}
                  </button>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-fire-line text-green-600"></i>
                    <span className="text-gray-600">Trending</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-arrow-up-line text-blue-600"></i>
                    <span className="text-gray-600">Moderate</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-star-line text-purple-600"></i>
                    <span className="text-gray-600">Niche</span>
                  </div>
                </div>

                <div className="grid gap-3">
                  {hashtags.map((hashtag, index) => (
                    <div
                      key={index}
                      className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-[#66b3ff] transition-all group"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-lg font-semibold text-[#22577A]">
                            {hashtag.tag}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(hashtag.type)}`}>
                            <i className={`${getTypeIcon(hashtag.type)} mr-1`}></i>
                            {hashtag.type}
                          </span>
                          <span className="text-sm text-gray-500">
                            {hashtag.reach} posts
                          </span>
                        </div>
                        <button
                          onClick={() => copyHashtag(hashtag.tag)}
                          className="px-3 py-2 bg-[#e4f4ff] text-[#22577A] rounded-lg font-medium hover:bg-[#b3d9ff] transition-all"
                        >
                          <i className="ri-file-copy-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Email Prompt */}
          {showEmailPrompt && (
            <div className="mt-8 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                Want hashtag analytics and tracking?
              </h3>
              <p className="text-white/90 mb-6">
                Sign up for MandaPost and track which hashtags drive the most engagement!
              </p>
              <a
                href="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all"
              >
                <i className="ri-rocket-line"></i>
                Start Free Trial
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#22577A] mb-4">Hashtag Best Practices</h2>
            <p className="text-xl text-gray-600">Get the most out of your hashtags</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="w-12 h-12 bg-[#e4f4ff] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-number-1 text-2xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Mix Popular & Niche</h3>
              <p className="text-gray-600">
                Use a combination of trending and niche hashtags to maximize reach while targeting your specific audience
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="w-12 h-12 bg-[#e4f4ff] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-number-2 text-2xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Use 5-10 Hashtags</h3>
              <p className="text-gray-600">
                Instagram allows up to 30, but 5-10 relevant hashtags typically perform best and look more authentic
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="w-12 h-12 bg-[#e4f4ff] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-number-3 text-2xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Stay Relevant</h3>
              <p className="text-gray-600">
                Always use hashtags that are directly related to your content to attract the right audience
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
